import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { type NonEmptyObject } from 'type-fest'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'

import { TrackEvent, useTracking } from '../../common/tracking'

import { AIAgentResult, AIAgentType } from '../../../../../models/AIAgent'
import { Scorecard } from '../../../../../models/Scorecard'
import { AiFeature } from '../../../../../models/AiFeature'
import { Me, User } from '../../../../../models/User'

import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { useExportToPDF } from '../../../../../components/PDFExportButton/hooks'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { Button } from '../../../../../components/Button/Button'

import { selectSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { selectFormFillStartedFrom } from '../../../../../store/meetingSummary/forms'
import { useLazyGetAiAgentResultQuery } from '../../../../../store/ai/aiAgentResult'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useExecuteAiAgentMutation } from '../../../../../store/ai/aiAgent'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'

import { getRoomAndAIFeatureTrackingDetails } from '../../tracking'

import { CitationExecutionProps, FormCitation, FormCitationExecutionResult } from './FormCitation/FormCitation'
import { useExecuteForm, useUpdateFormResult } from './hooks'
import { AddFormToCRMButtons } from './AddFormToCRMButtons'
import { FormFieldView } from './FormFieldView'

import './FormResult.scss'
import { FormResultDownloadButton } from './FormResultDownloadButton'

interface FormClientResultProps {
  formResult: AIAgentResult
  user: User | null
  aiFeature: AiFeature
  scorecard: Scorecard | null
  currentUser: Me | null
  saveEventDetails: NonEmptyObject
  exportEventDetails: NonEmptyObject
}

interface FormValues {
  formResultToDisplay: NonEmptyObject
  metadata: string
  citations: FormCitation | null
  edited: boolean
}

const PDF_STYLES = `
  .disabled-contact-information-field {
   background: #fff !important;  
 }`

export const FormResult: React.FC<FormClientResultProps> = ({ formResult, user, aiFeature, scorecard, currentUser, saveEventDetails, exportEventDetails }) => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const { tracked } = useTracking()

  const [triggerGetAiAgentResult, aiAgentResultQuery] = useLazyGetAiAgentResultQuery()

  const formRefToPdf = useRef(null)
  const { exportToPDF } = useExportToPDF(formRefToPdf, PDF_STYLES)
  const { updateFormResult, updateLoading } = useUpdateFormResult()
  const executeForm = useExecuteForm()

  const [editing, setEditing] = useState<boolean>(false)
  const [currentSavedValues, setCurrentSavedValues] = useState(formResult.result)
  const [validatorForFields, setValidatorForFields] = useState<{ [key: string]: boolean }>({})

  const form = useForm<FormValues>({
    defaultValues: {
      formResultToDisplay: formResult.result,
      metadata: formResult.metadata,
      citations: null,
      edited: formResult.updatedAt > formResult.createdAt,
    },
  })

  const getCitationAgentResult = useCallback(() => {
    return triggerGetAiAgentResult(
      {
        accountId,
        type: AIAgentType.FORM_CITATION,
        resultKey: `citation|${formResult.resultKey}`,
      },
      false,
    ).unwrap()
  }, [accountId, formResult.resultKey, triggerGetAiAgentResult])

  const getCitations = useCallback(async () => {
    const aiAgentResult = await getCitationAgentResult()
    const citations = aiAgentResult && (aiAgentResult.result as FormCitation | null)
    if (!citations) {
      return
    }
    form.setValue('citations', citations)
  }, [form, getCitationAgentResult])

  useEffect(() => {
    getCitations()
  }, [getCitations])

  const [triggerExecuteAiAgent, executeAiAgentMutation] = useExecuteAiAgentMutation()

  const _executeCitations = useCallback(
    (context: CitationExecutionProps) => {
      return triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.FORM_CITATION,
          context,
        },
      }).unwrap() as Promise<FormCitationExecutionResult>
    },
    [triggerExecuteAiAgent, accountId],
  )

  const executeCitations = tracked(_executeCitations, {
    eventName: TrackEvent.FORM_CITATION_GENERATED,
    eventDetails: {
      resultId: formResult?.id,
      scorecardId: scorecard?.id,
      type: roomSession ? 'form' : 'clientForm',
      ...(roomSession && { sessionId: roomSession.id, roomId: roomSession.roomId }),
    },
  })

  const _getExecutedCitations = useCallback(async () => {
    const citations = form.getValues('citations')
    if (!citations && user && formResult && scorecard) {
      const context: CitationExecutionProps = {
        contactId: user?.id,
        resultId: formResult.id,
        scorecardId: scorecard?.id,
      }

      if (roomSession?.id) {
        context.sessionId = roomSession.id
      }

      if (roomSession?.roomId) {
        context.roomId = roomSession.roomId
      }
      return executeCitations(context)
    }
  }, [form, user, formResult, scorecard, roomSession?.id, roomSession?.roomId, executeCitations])

  const getExecutedCitations = tracked(_getExecutedCitations, {
    eventName: TrackEvent.FORM_CITATION_CLICKED,
    eventDetails: {
      resultId: formResult?.id,
      scorecardId: scorecard?.id,
      type: roomSession ? 'form' : 'clientForm',
      ...(roomSession && { sessionId: roomSession.id, roomId: roomSession.roomId }),
    },
  })

  const onExecuteCitation = async () => {
    const result = await getExecutedCitations()
    if (result && result.result && result.result.citations) {
      form.setValue('citations', result.result)
    }
  }

  useEffect(() => {
    setCurrentSavedValues(formResult.result)
    form.reset({
      formResultToDisplay: formResult.result,
      metadata: formResult.metadata,
      edited: formResult.updatedAt > formResult.createdAt,
      citations: form.getValues('citations'),
    })
    setEditing(false)
  }, [form, formResult.createdAt, formResult.metadata, formResult.result, formResult.updatedAt])

  const updateFieldValidation = (fieldKey: string, isValid: boolean) => {
    setValidatorForFields((prev) => ({
      ...prev,
      [fieldKey]: isValid,
    }))
  }
  const onEdit = () => {
    setEditing(true)
    setValidatorForFields({})
  }

  const _handleSave = async (data: FormValues) => {
    try {
      await updateFormResult(formResult.id, data.formResultToDisplay)
      setCurrentSavedValues(data.formResultToDisplay)
    } finally {
      setEditing(false)
    }
  }

  const handleSave = tracked(
    _handleSave,
    {
      eventName: TrackEvent.FORM_RESULT_EDITED,
      eventDetails: { ...saveEventDetails, source: useAppSelector(selectFormFillStartedFrom) },
    },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return { formResult: data.formResultToDisplay }
      },
    },
  )
  const handleExportToPdf = tracked(exportToPDF, {
    eventName: TrackEvent.EXPORT_TO_PDF,
    eventDetails: exportEventDetails,
  })

  const onCancel = () => {
    setEditing(false)
    form.reset({
      formResultToDisplay: currentSavedValues,
      metadata: formResult.metadata,
      citations: form.getValues('citations'),
      edited: formResult.updatedAt > formResult.createdAt,
    })
  }

  const descriptorSchema = scorecard?.descriptor

  if (!descriptorSchema) {
    return null
  }

  const isFormUpToDate = scorecard?.updatedAt < formResult.updatedAt

  const _handleRun = async () => {
    await executeForm(aiFeature.id)
    form.setValue('citations', null)
  }

  const handleRun = tracked(_handleRun, {
    eventName: TrackEvent.FORM_MANUALLY_EXECUTED,
    eventDetails: getRoomAndAIFeatureTrackingDetails(roomSession, aiFeature),
  })

  const isCitationLoading = aiAgentResultQuery.isLoading || executeAiAgentMutation.isLoading
  const userName = user?.name ?? 'Everyone on meeting'
  const allValid = Object.values(validatorForFields).every((valid) => valid)

  return (
    <FormProvider {...form}>
      <div
        className={classNames('p-3 rounded-2 border zocks-border-color-gray-300', {
          'zocks-bg-color-gray-100': editing,
        })}
      >
        {!isFormUpToDate ? (
          <div className='d-flex align-self-center gap-2 zocks-bg-color-primary-light p-2 border rounded-2 align-items-center'>
            <i className='bi bi-exclamation-circle' />
            <span className='zocks-text-gray-900'>The form configuration has changed. Please rerun the form to see updated content.</span>
            {selectedMeetingTemplate ? (
              <ScopedBlock
                scope={{
                  scope: 'aifeature:execute',
                  superScope: ['aifeature:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button variant='primary' className='button-small py-1 px-2' onClick={handleRun}>
                  <i className='bi bi-arrow-repeat'></i>
                  Rerun
                </Button>
              </ScopedBlock>
            ) : null}
          </div>
        ) : null}
        <Row className='d-flex align-items-center pb-2'>
          <Col xs='auto' className='py-4'>
            {user ? <i className='bi bi-person-fill' /> : null}
            <span className='bold paragraph-large ps-2'>{userName}</span>
          </Col>
          <Col className='d-flex p-3 align-items-center gap-2 justify-content-end'>
            {!editing ? (
              <>
                <AddFormToCRMButtons aiFeature={aiFeature} formResult={formResult} contact={user} currentUser={currentUser} />
                <Button className='px-2 py-1 button-medium' variant='header' onClick={handleExportToPdf}>
                  Export
                </Button>
                <FormResultDownloadButton scorecard={scorecard} formResult={formResult} />
                <Button variant='primary' className='px-2 py-1 button-medium' onClick={onEdit} disabled={isCitationLoading}>
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button variant='secondary' className='px-2 py-1 button-medium' onClick={onCancel} disabled={updateLoading}>
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  className='px-2 py-1 button-medium'
                  onClick={form.handleSubmit(handleSave)}
                  loading={updateLoading}
                  disabled={!allValid}
                >
                  Save
                </Button>
              </>
            )}
          </Col>
        </Row>
        <Row className='px-2' ref={formRefToPdf}>
          {Object.entries(descriptorSchema?.properties ?? {})
            .map(([key, schema]) => ({ key, schema }))
            .sort((a, b) => (a.schema.index ?? 0) - (b.schema.index ?? 0))
            .map(({ key, schema }) => {
              const fieldKey = user ? `${user.id}-${aiFeature.id}-${key}` : `${aiFeature.id}-${key}`
              return (
                <FormFieldView
                  key={`${fieldKey}-${editing}`}
                  formKey={fieldKey}
                  schema={schema}
                  label={key}
                  isEditing={editing}
                  disabled={updateLoading}
                  uiSchema={descriptorSchema?.customWidgets && descriptorSchema.customWidgets[key] ? descriptorSchema?.customWidgets[key] : undefined}
                  setValidatorSucceeded={(isValid) => updateFieldValidation(fieldKey, isValid)}
                  onExecuteCitation={onExecuteCitation}
                  isCitationLoading={isCitationLoading}
                />
              )
            })}
        </Row>
      </div>
    </FormProvider>
  )
}
